import React, { useState, useEffect } from 'react';
import { SyncLoader } from 'react-spinners';
import { getFunctions, httpsCallable } from 'firebase/functions';

const CreateMissionAI = ({ isOpen, onClose, onMissionGenerated, gameLocation, gameConcepts }) => {
    const [generating, setGenerating] = useState(false);
    const [selectedConcepts, setSelectedConcepts] = useState([]);
    const [location, setLocation] = useState(gameLocation || '');
    const [missionRequest, setMissionRequest] = useState('');
    const [difficulty, setDifficulty] = useState('普通');
    const [missionType, setMissionType] = useState('探索');
    const [error, setError] = useState('');

    const conceptsOptions = [
        '学生向け', '家族向け', '子ども向け', '大人向け', 
        'かんたん', 'ハード', 'マニアック', '鉄道好き', 
        '自然好き', '歴史好き', '食べ物', '観光地'
    ];

    const difficultyOptions = ['やさしい', '普通', '難しい'];
    const typeOptions = ['探索', '謎解き', 'チームワーク', '知識', '体験', 'コミュニケーション'];

    // 開催地の初期値設定
    useEffect(() => {
        if (gameLocation) {
            setLocation(gameLocation);
        }
    }, [gameLocation]);

    // コンセプト選択の切り替え
    const toggleConcept = (index) => {
        const newConcepts = [...selectedConcepts];
        if (newConcepts.includes(index)) {
            newConcepts.splice(newConcepts.indexOf(index), 1);
        } else {
            newConcepts.push(index);
        }
        setSelectedConcepts(newConcepts);
    };

    // ミッション生成
    const generateMission = async () => {
        if (!location.trim()) {
            setError('開催場所を入力してください。');
            return;
        }

        setGenerating(true);
        setError('');

        try {
            const selectedConceptNames = selectedConcepts.map(index => conceptsOptions[index]);
            
            // Firebase Functionsを使用してAI生成を呼び出し
            const functions = getFunctions();
            const generateMissionFunction = httpsCallable(functions, 'generateMission');
            
            const result = await generateMissionFunction({
                location: location,
                concepts: selectedConceptNames,
                difficulty: difficulty,
                missionType: missionType,
                missionRequest: missionRequest
            });

            const missionData = result.data.mission;
            
            // データの検証
            if (!missionData.name || !missionData.detail) {
                throw new Error('生成されたデータが不完全です');
            }

            // 親コンポーネントにミッションデータを渡す
            onMissionGenerated(missionData);
            onClose();

        } catch (error) {
            console.error('ミッション生成エラー:', error);
            setError('ミッションの生成中にエラーが発生しました。しばらく時間をおいて再度お試しください。');
        } finally {
            setGenerating(false);
        }
    };

    // モーダルが開いていない場合は何も表示しない
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold text-gray-800">
                            <i className="bi bi-stars me-2 text-purple-600"></i>
                            AI でミッション生成
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 text-2xl"
                            disabled={generating}
                        >
                            <i className="bi bi-x"></i>
                        </button>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                            {error}
                        </div>
                    )}

                    <div className="space-y-6">
                        {/* 開催場所 */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                開催場所 <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-purple-300"
                                placeholder="例: 渋谷駅周辺、○○公園、××商店街"
                                disabled={generating}
                            />
                        </div>

                        {/* コンセプト選択 */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                コンセプト（複数選択可）
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                                {conceptsOptions.map((concept, index) => (
                                    <label key={index} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={selectedConcepts.includes(index)}
                                            onChange={() => toggleConcept(index)}
                                            className="mr-2"
                                            disabled={generating}
                                        />
                                        <span className="text-sm">{concept}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* 難易度 */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                難易度
                            </label>
                            <select
                                value={difficulty}
                                onChange={(e) => setDifficulty(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-purple-300"
                                disabled={generating}
                            >
                                {difficultyOptions.map((option) => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>

                        {/* ミッションタイプ */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                ミッションタイプ
                            </label>
                            <select
                                value={missionType}
                                onChange={(e) => setMissionType(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-purple-300"
                                disabled={generating}
                            >
                                {typeOptions.map((option) => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>

                        {/* 自由入力欄 */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                どんなミッションがいいですか？（自由記述）
                            </label>
                            <textarea
                                value={missionRequest}
                                onChange={(e) => setMissionRequest(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-purple-300 h-24"
                                placeholder="例: チームワークを重視したミッション、地域の歴史を学べる内容、写真撮影を含むもの など"
                                disabled={generating}
                            />
                        </div>
                    </div>

                    {/* ボタン */}
                    <div className="flex justify-end space-x-3 mt-8 pt-4 border-t">
                        <button
                            onClick={onClose}
                            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            disabled={generating}
                        >
                            キャンセル
                        </button>
                        <button
                            onClick={generateMission}
                            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center"
                            disabled={generating || !location.trim()}
                        >
                            {generating ? (
                                <>
                                    <SyncLoader color="#ffffff" size={8} className="mr-2" />
                                    生成中...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-stars mr-2"></i>
                                    ミッション生成
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateMissionAI;